/**
 * cart-mirror.js — mirrors every cart mutation up to the account's cart.
 *
 * Design: docs/superpowers/specs/2026-09-02-cart-sync-service-owned-cart-design.md
 *
 * The account's cart belongs to the service and is named by `custom.cart`. This
 * browser's Shopify cookie cart is a WORKING COPY — checkout runs from it, and
 * nothing else about it is authoritative. This module carries changes in one
 * direction only, working copy -> account, and is the whole of that direction.
 *
 * What goes on the wire is INTENT, not a delta: "the line <merchandise + this
 * exact attribute set> is now at quantity N", where 0 means remove. Two
 * properties follow and both are load-bearing:
 *
 *   - Idempotent. A replayed intent cannot double a quantity, which is what
 *     lets this retry freely and lets the debounce below collapse a stepper's
 *     repeats. (A delta could do neither.)
 *   - It cannot clobber. It names one line, so it can never delete a line the
 *     Flutter app added a moment earlier. The concurrency problem does not
 *     exist rather than being mitigated, and nothing has to be negotiated with
 *     the app — no version, no etag.
 *
 * Public surface, all for cart-sync.js:
 *   window.CasinettoCartMirror.flush()          -> Promise, always resolves
 *   window.CasinettoCartMirror.suppress()       stop observing (logout clear)
 *   window.CasinettoCartMirror.resume()
 *   window.CasinettoCartMirror.adopt(cart)      snapshot a reconciled cart
 *   window.CasinettoCartMirror.pushAll()        push the whole working copy
 *   window.CasinettoCartMirror.pending()        -> queued intent count
 */
(function () {
  'use strict';

  var LS_QUEUE   = 'casinetto_cart_mirror_queue';
  var ENDPOINT   = '/apps/cart-sync/lines';
  var DEBOUNCE_MS = 300;
  var TIMEOUT_MS  = 5000;
  var BACKOFF_MS  = [1000, 4000, 15000];

  var _script = document.querySelector('script[data-cart-mirror]');
  var _customerId = '';
  if (_script) { _customerId = (_script.getAttribute('data-customer-id') || '').trim(); }

  // The WORKING COPY as of the last mirror, and never anything else. IN-PAGE
  // ONLY — initialised from /cart.js on load, never persisted.
  //
  // The name and this comment are load-bearing, because the variable held two
  // meanings for a day and the second one deleted data. `onApplied` used to set
  // it from a lines response, which is the ACCOUNT CART's state — and the diff
  // in observe() compares /cart.js against it. So: the account cart holds X at
  // 1 because another device added it, this browser's working copy has no X,
  // and the next local change diffs a snapshot containing X against a working
  // copy without it and sends "X -> 0". It REMOVED the other device's line.
  //
  // So: this is the working copy, and tracking the server in it is what makes
  // the diff mean something other than "what changed here". If the account
  // cart's state is ever needed, it needs its own variable and its own reason.
  //
  // Also NOT the merge base the design rejected: that one had to survive page
  // loads and be consulted BEFORE reconciling, to decide who wins per line.
  // Persisting this, or reading it during reconciliation, silently
  // reintroduces the three-way merge nobody approved.
  var _lastMirrored = null;

  // True while a flush is carrying the WHOLE working copy rather than a diff.
  // The service sets `cartReplaced` on every repoint — foreign stamp, expired
  // pointed cart, and no pointer at all — so a first login answers a full push
  // with "replaced", and re-pushing on that signal would send the same set
  // twice. Not a loop (the second write is in-place, so it comes back without
  // the flag) but a wasted round trip per first login, and in a log it reads
  // like a bug at exactly the moment someone is trying to tell one from the
  // other.
  var _pushedAll = false;

  // Set while a whole-working-copy push is being assembled, and it strips
  // `expectedQuantity` from those intents.
  //
  // The rule, and it is simpler than a special case: **a diff carries a base, a
  // whole-copy push does not.** A diff says "this line moved from N to M" and a
  // base is exactly what makes that checkable. A whole-copy push says "whatever
  // is there, the working copy is the truth" — it is asserted, not proposed, and
  // it is only ever reached when we have NO belief about the account's cart: no
  // pointer, a dead one, or a cart the service just replaced.
  //
  // Without this, the recovery from a substitution defeats itself. The re-push
  // computes bases from `_lastMirrored = {}`, so every line claims an expected
  // 0, while the replaced cart already holds whatever the substituting request
  // carried. Those lines get refused as stale, the rejection reconciles, and
  // the adopt cuts the working copy down to exactly the truncated cart the
  // re-push existed to repair. (cas-tech made the same call server-side for
  // cart creation, for the same reason, and their note is what surfaced this.)
  var _assertWholeCopy = false;

  var _suppressed = false;
  var _timer = null;
  var _inFlight = null;
  var _attempt = 0;

  function warn() {
    var args = ['[cart-mirror]'].concat(Array.prototype.slice.call(arguments));
    console.warn.apply(console, args);
  }

  /* ---- Storage ---------------------------------------------------- */

  // The queue IS persisted, unlike the snapshot, and for a concrete reason: on
  // a grocery site people add and navigate constantly. Held in memory, "add
  // milk -> network hiccup -> click through to the next page" loses the milk,
  // and under `adopt the account's cart` the next reconciliation would then
  // quietly revert it. That is a common path, not an edge case.
  function readQueue() {
    try {
      var raw = localStorage.getItem(LS_QUEUE);
      if (!raw) { return {}; }
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') { return {}; }
      return parsed;
    } catch (_) {
      return {};
    }
  }

  function writeQueue(queue) {
    try {
      if (!Object.keys(queue).length) {
        localStorage.removeItem(LS_QUEUE);
        return;
      }
      localStorage.setItem(LS_QUEUE, JSON.stringify(queue));
    } catch (_) { /* private mode / quota — the mirror degrades, nothing breaks */ }
  }

  /* ---- Shapes ----------------------------------------------------- */

  // /cart.js gives line properties as an OBJECT; the wire (and the Storefront
  // API behind it) wants an ARRAY of {key, value}. Every property is carried,
  // `_`- and `__`-prefixed included: measured 2026-09-02 that /cart.js and the
  // Storefront API report the same set with the same values, underscores and
  // all, so dropping any of them here would make this browser's view of a line
  // disagree with the service's.
  function propertiesToAttributes(properties) {
    var attributes = [];
    if (!properties) { return attributes; }
    Object.keys(properties).forEach(function (key) {
      var value = properties[key];
      attributes.push({ key: key, value: value == null ? '' : String(value) });
    });
    return attributes;
  }

  function snapshotOf(cart) {
    var byKey = {};
    var items = (cart && cart.items) || [];
    items.forEach(function (item) {
      var key = window.CasinettoCart.buildLineKey(item.variant_id, item.properties);
      var attributes = propertiesToAttributes(item.properties);
      var existing = byKey[key];
      if (!existing) {
        byKey[key] = {
          merchandiseId: 'gid://shopify/ProductVariant/' + item.variant_id,
          attributes: attributes,
          quantity: item.quantity
        };
        return;
      }
      // Two /cart.js items CAN share a key, now that analytics attributes are
      // excluded from identity: the app's line and the web's line for one
      // product are two physical lines and one logical one. Sum them — the
      // product rule is one line per product (gelato per flavour set aside), so
      // a collision is a duplicate to be folded, not two things.
      existing.quantity += item.quantity;
      // Which attribute set to send on the wire, when they differ: the richer
      // one, because the service still keys on the COMPLETE set, so an intent
      // carrying the emptier set would miss the line that has attributes and
      // create yet another. A tie-break, not a rule — the duplicate itself is
      // transient, since reconciliation collapses it (adoptInto).
      if (attributes.length > existing.attributes.length) {
        existing.attributes = attributes;
      }
    });
    return byKey;
  }

  /* ---- Diff ------------------------------------------------------- */

  // Intents, not deltas: each one states where a line should END UP. So the
  // diff never needs to be exact — a missed event costs a stale line until that
  // line changes again, not a wrong quantity forever.
  function diff(before, after) {
    var intents = [];
    var key;

    for (key in after) {
      if (!Object.prototype.hasOwnProperty.call(after, key)) { continue; }
      var now = after[key];
      var was = before ? before[key] : null;
      if (!was || was.quantity !== now.quantity) {
        intents.push({
          key: key,
          merchandiseId: now.merchandiseId,
          attributes: now.attributes,
          quantity: now.quantity,
          // What we believe the ACCOUNT's cart holds for this line. Sound
          // because `_lastMirrored` is our record of what we told the service
          // to make true: after a reconciliation it equals the account's cart,
          // and after a local change it equals what we asked for.
          expectedQuantity: was ? was.quantity : 0
        });
      }
    }

    for (key in (before || {})) {
      if (!Object.prototype.hasOwnProperty.call(before, key)) { continue; }
      if (after[key]) { continue; }
      // Gone from the working copy -> quantity 0, which the endpoint reads as
      // a removal. Carrying the old attributes is what lets the service match
      // the line it is being asked to remove.
      intents.push({
        key: key,
        merchandiseId: before[key].merchandiseId,
        attributes: before[key].attributes,
        quantity: 0,
        expectedQuantity: before[key].quantity
      });
    }

    return intents;
  }

  function enqueue(intents) {
    if (!intents.length) { return false; }
    var queue = readQueue();
    intents.forEach(function (intent) {
      // A newer intent for the same line REPLACES the older one rather than
      // queueing behind it. Sound only because these are intents: the latest
      // one states the whole truth about that line, so the ones it displaces
      // had nothing left to say.
      var row = {
        merchandiseId: intent.merchandiseId,
        attributes: intent.attributes,
        quantity: intent.quantity
      };
      // Absent, not null: an asserted intent must not claim a base at all.
      if (intent.expectedQuantity != null) {
        row.expectedQuantity = intent.expectedQuantity;
      }
      queue[intent.key] = row;
    });
    writeQueue(queue);
    return true;
  }

  /* ---- Send ------------------------------------------------------- */

  function postLines(lines) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ lines: lines }),
      signal: controller.signal
    })
      .then(function (response) {
        return response.json()
          .catch(function () { return null; })
          .then(function (body) {
            return { ok: response.ok, status: response.status, body: body };
          });
      })
      .finally(function () { clearTimeout(timer); });
  }

  // Which failures are worth retrying, per the service's error classes. NOT
  // keyed on `retryable` — contract §3b says that field is not the
  // discriminator on the cart routes and defaults to false even for a
  // transient, so keying on it would make an ownership refusal and a hiccup
  // indistinguishable.
  function isRetryable(result) {
    if (!result) { return true; }                       // network / abort
    if (result.status === 401 || result.status === 400) { return false; }
    var code = result.body && result.body.errorCode;
    if (code === 'BAD_SIGNATURE' || code === 'NOT_LOGGED_IN' || code === 'INVALID_LINES') {
      return false;
    }
    return true;
  }

  function flush() {
    if (_inFlight) { return _inFlight; }
    if (!_customerId) { return Promise.resolve(); }

    var queue = readQueue();
    var keys = Object.keys(queue);
    if (!keys.length) { return Promise.resolve(); }

    var lines = keys.map(function (key) {
      var line = {
        merchandiseId: queue[key].merchandiseId,
        attributes: queue[key].attributes,
        quantity: queue[key].quantity
      };
      if (queue[key].expectedQuantity != null) {
        line.expectedQuantity = queue[key].expectedQuantity;
      }
      return line;
    });

    _inFlight = postLines(lines)
      .catch(function () { return null; })
      .then(function (result) {
        if (result && result.ok) {
          onApplied(keys, result.body);
          _attempt = 0;
          // Anything enqueued WHILE this request was in flight is still in the
          // queue: the early `if (_inFlight) return _inFlight` above hands the
          // caller the running promise and does not send the new intents. So
          // without this, they sat there until the next cart:update happened to
          // flush them — and if the shopper did nothing else they never went up
          // at all, while the working copy showed them. The next reconciliation
          // then ADOPTS and removes them from the working copy: silent loss.
          //
          // Only on the success path. A retryable failure already has its own
          // backoff retry, and re-flushing here too would double it.
          if (Object.keys(readQueue()).length) {
            setTimeout(function () { flush(); }, 0);
          }
          return;
        }
        if (!isRetryable(result)) {
          // Nothing a retry can change. Dropping loses the intent, but keeping
          // it means the queue never drains and every later intent for other
          // lines is stuck behind a request that will always be refused.
          warn('dropping ' + keys.length + ' unsendable intent(s)', result && result.status);
          dropKeys(keys);
          _attempt = 0;
          return;
        }
        _attempt += 1;
        var delay = BACKOFF_MS[Math.min(_attempt - 1, BACKOFF_MS.length - 1)];
        warn('mirror failed, retrying in ' + delay + 'ms', result && result.status);
        setTimeout(function () { flush(); }, delay);
      })
      .finally(function () { _inFlight = null; });

    return _inFlight;
  }

  function dropKeys(keys) {
    var queue = readQueue();
    keys.forEach(function (key) { delete queue[key]; });
    writeQueue(queue);
  }

  function onApplied(sentKeys, body) {
    // A line the service refused (merchandise gone, not purchasable) comes back
    // `rejected`. Drop it rather than retrying: without the per-line outcome a
    // single dead line would fail the whole batch forever and the other intents
    // would never land.
    // Two kinds of rejection, and confusing them is how a basket goes wrong.
    //
    //   `actualQuantity` PRESENT -> the base we sent was stale. Somebody else
    //   moved that line, or our own earlier write landed and its response was
    //   lost. **Reconcile; never re-derive the intent locally.** The two causes
    //   are indistinguishable from here — both read as "expected 0, actual 1" —
    //   and re-deriving picks one and is silently wrong on the other.
    //
    //   `actualQuantity` ABSENT -> Shopify refused the line itself (merchandise
    //   gone, not purchasable). Nothing a retry changes: drop it.
    var results = (body && body.results) || [];
    var staleBase = false;
    results.forEach(function (row) {
      if (!row || row.status !== 'rejected') { return; }
      if (row.actualQuantity != null) {
        staleBase = true;
        warn('stale base for', row.merchandiseId, '- actual', row.actualQuantity);
        return;
      }
      warn('line rejected by the service', row.merchandiseId, row.reason || '');
    });

    dropKeys(sentKeys);

    if (staleBase && window.CasinettoCartSync && window.CasinettoCartSync.reconcile) {
      // Dropped AND reconciled: keeping the intent would retry the same stale
      // base forever, and the reconciliation is what makes the next diff
      // compute from a base that is actually true.
      window.CasinettoCartSync.reconcile();
    }

    // A SUBSTITUTION (contract §3b): the pointed cart was stamped by another
    // identity, so the service made a fresh one, applied these intents to it,
    // and repointed. The new cart therefore holds ONLY what was just sent —
    // and what was just sent is a diff, not the basket. Believing the response
    // here would set the snapshot to that partial set, and since a diff only
    // produces intents when something changes, the rest of the working copy
    // would sit unsent until the next reconciliation ADOPTED the partial cart
    // and cut the basket down to it.
    //
    // So push everything instead. The theme cannot detect this on its own: it
    // never names a cart, so it has no id to compare. The signal has to come
    // from the service, which is what §3b's `cartReplaced` is for.
    if (body && body.cartReplaced) {
      if (_pushedAll) {
        // Already sent the whole working copy, and a repoint carries all of it,
        // so a second push would add nothing.
        _pushedAll = false;
        return;
      }
      // The account's cart is now a FRESH cart holding only what this request
      // carried — which was a diff, not the basket. So the rest of the working
      // copy has to follow, and emptying `_lastMirrored` is how: every line then
      // reads as changed, and the intents for lines the new cart already has are
      // idempotent no-ops.
      warn('cart was replaced by the service — pushing the whole working copy');
      _lastMirrored = {};
      _assertWholeCopy = true;
      setTimeout(function () { observe(); }, 0);
      return;
    }
    _pushedAll = false;

    // `_lastMirrored` is deliberately NOT touched here. observe() already
    // advanced it to the working copy that produced these intents, which is
    // exactly what "last mirrored" means. Setting it from the response instead
    // — which cas-tech suggested and I took, on the reasoning that the snapshot
    // would then be server truth and could not drift — is what turned this
    // variable into a deletion path: the response is the ACCOUNT cart, and
    // diffing the working copy against the account cart emits removals for
    // every line the account has and this browser does not.
    //
    // The response's own read-back also degrades on purpose (the write already
    // landed, and reporting a failure would make this queue retry a successful
    // write), so a 200 can carry a missing or partial cart. One more reason not
    // to believe it here.
  }

  /* ---- Observing the working copy --------------------------------- */

  function observe() {
    if (_suppressed || !_customerId) { return; }
    if (!window.CasinettoCart || !window.CasinettoCart.fetchCart) { return; }

    return window.CasinettoCart.fetchCart()
      .then(function (cart) {
        var after = snapshotOf(cart);
        // A NULL snapshot means "I do not know what I last mirrored", and it
        // must claim nothing: seed and stop. An EMPTY one means "I mirrored
        // nothing", which is a claim, and pushAll() sets it deliberately.
        //
        // Not a nicety — it closes a race. The seed below is async, and
        // cart-sync flushes this module before reconciling. Diffing against
        // null there would read every line as new and push a LEFTOVER working
        // copy into the account's cart, before the adopt that was about to
        // overwrite it.
        if (_lastMirrored === null) {
          _lastMirrored = after;
          return null;
        }
        var intents = diff(_lastMirrored, after);
        if (_assertWholeCopy) {
          _assertWholeCopy = false;
          intents.forEach(function (intent) { delete intent.expectedQuantity; });
        }
        _lastMirrored = after;
        if (enqueue(intents)) { return flush(); }
        return null;
      })
      .catch(function (err) {
        // The snapshot is deliberately left alone: without knowing the current
        // state there is nothing to diff against, and guessing would either
        // re-send everything or miss the change entirely. The next event
        // re-reads and the diff picks up whatever happened in between.
        warn('could not read the working copy', err);
      });
  }

  function onCartUpdate(event) {
    if (_suppressed) { return; }
    // The logout clear emits cart:update like any other change. Mirroring it
    // would empty the ACCOUNT's cart — D4's data loss wearing a new coat — so
    // cart-sync suppresses this module around that clear. The source check is a
    // second line of defence, not the first.
    var source = event && event.detail && event.detail.data && event.detail.data.source;
    if (source === 'cart-sync-release') { return; }

    if (_timer) { clearTimeout(_timer); }
    // Debounce: a stepper pressed three times fires three events and sends one
    // intent with the final quantity. Safe only because intents state where a
    // line ends up — debouncing deltas would drop two increments.
    _timer = setTimeout(function () { _timer = null; observe(); }, DEBOUNCE_MS);
  }

  /* ---- Public API ------------------------------------------------- */

  window.CasinettoCartMirror = {
    flush: function () {
      if (_timer) { clearTimeout(_timer); _timer = null; }
      // Observe first: a debounce may still be holding an unsent change, and
      // cart-sync calls this before reconciling — reconciling with an unsent
      // intent means undoing it.
      return Promise.resolve(observe()).then(function () { return flush(); });
    },
    suppress: function () { _suppressed = true; if (_timer) { clearTimeout(_timer); _timer = null; } },
    resume: function () { _suppressed = false; },
    adopt: function (cart) { _lastMirrored = snapshotOf(cart); },

    // Push the WHOLE working copy, not the diff. Needed after an anonymous
    // merge: the snapshot was seeded from the pre-merge cart, so a diff would
    // name only the lines the merge ADDED -- which are the account's own lines
    // and therefore no-ops -- and the anonymous lines, the entire point of the
    // merge, would never reach the account's cart.
    //
    // Emptying the snapshot rather than adding a second code path: every line
    // then reads as changed, and the intents for lines the service already has
    // are idempotent no-ops. Same mechanism, no special case.
    pushAll: function () {
      _lastMirrored = {};
      _pushedAll = true;
      _assertWholeCopy = true;
      return Promise.resolve(observe());
    },
    pending: function () { return Object.keys(readQueue()).length; }
  };

  /* ---- Init ------------------------------------------------------- */

  function start() {
    document.addEventListener('cart:update', onCartUpdate);
    if (!_customerId) { return; }
    if (!window.CasinettoCart || !window.CasinettoCart.fetchCart) {
      warn('CasinettoCart unavailable, mirror inert');
      return;
    }
    // Seed the snapshot from the working copy rather than from a reconciliation
    // that may not run this page load: with the queue drained, the cookie cart
    // IS what was last mirrored, so it is the correct starting point.
    window.CasinettoCart.fetchCart()
      .then(function (cart) { _lastMirrored = snapshotOf(cart); })
      .catch(function (err) { warn('could not seed the snapshot', err); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
